import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow,background-color,border-color] duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 focus-visible:outline-3 focus-visible:outline-accent focus-visible:outline-offset-2 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover',
        destructive: 'bg-destructive text-white shadow-xs hover:bg-primary-hover',
        outline: 'border-2 border-solid border-primary bg-transparent text-primary hover:border-foreground hover:text-foreground py-4',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-primary-hover hover:text-white',
        ghost: 'hover:bg-primary-hover hover:text-white',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-6 py-2',
        xs: 'h-7 rounded-md px-4',
        sm: 'h-8 rounded-md gap-1.5 px-4',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Affiche un spinner et désactive le bouton. */
    loading?: boolean;
    /** Libellé affiché pendant le chargement (à défaut, les enfants). */
    loadingText?: React.ReactNode;
  };

function Button({ className, variant, size, asChild = false, loading = false, loadingText, children, disabled, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';

  const content = loading ? (
    <>
      <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      {loadingText || children}
    </>
  ) : (
    children
  );

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled || loading}
      {...props}
    >
      {content}
    </Comp>
  );
}

export { Button, buttonVariants, type ButtonProps };
